import { useEffect, useSyncExternalStore } from 'react';
import { reportOptionsService } from '@/core/services/reportOptions';

/** ClientReportOptions.AuditStatus: 2 audited; 1 audit exempt; empty never chosen (audit exempt). */
const AUDITED = 2;
const DORMANT = 1;

export const ACCOUNTANTS_REPORT_TITLE = 'Accountant’s Report';
export const AUDITORS_REPORT_TITLE = 'Auditor’s Report';

/**
 * #200: whether the client's accounts are audited, as the accounts themselves decide it - audit status 2 (Audited)
 * and not dormant; dormant accounts are always audit exempt (#88, Report Settings).
 */
export function isAuditedStatus(auditStatus: number | null | undefined, tradingStatus?: number | null): boolean {
  return auditStatus === AUDITED && tradingStatus !== DORMANT;
}

/** The report's name: the auditor's report for audited accounts, otherwise the accountant's report. */
export function accountantReportTitle(audited: boolean): string {
  return audited ? AUDITORS_REPORT_TITLE : ACCOUNTANTS_REPORT_TITLE;
}

/**
 * Each client's audit status as Report Settings last loaded or chose it, shared so the Settings menu and the
 * Accountant's Report page follow a change made on Report Settings at once (the CIC34 menu item's per-client cache,
 * with change notification).
 */
const auditedByClient = new Map<string, boolean>();
const loading = new Set<string>();
const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => void listeners.delete(listener);
};

/** Records a client's audit status; Report Settings calls this whenever its audit status is loaded or changed. */
export function setClientAudited(clientId: string, audited: boolean): void {
  if (auditedByClient.get(clientId) === audited) return;
  auditedByClient.set(clientId, audited);
  listeners.forEach((listener) => listener());
}

export function resetAuditStatusCache(): void {
  auditedByClient.clear();
  loading.clear();
  listeners.forEach((listener) => listener());
}

/**
 * The name of the client's accountant's / auditor's report, read once per client from Report Settings
 * (ReportOptions/getReportSettings) and kept in step with changes made there. Unknown or unreadable reads as not
 * audited, as the accounts do.
 */
export function useAccountantReportTitle(clientId: string | null | undefined): string {
  return accountantReportTitle(useClientAudited(clientId));
}

/** Whether the client's accounts are audited (BUG-075: the Auditor's Report screen and Additional Notes follow it). */
export function useClientAudited(clientId: string | null | undefined): boolean {
  const audited = useSyncExternalStore(subscribe, () => (clientId ? (auditedByClient.get(clientId) ?? false) : false));

  useEffect(() => {
    if (!clientId || auditedByClient.has(clientId) || loading.has(clientId)) return;
    loading.add(clientId);
    // Started inside the chain so any failure to read, however it fails, only leaves the default name.
    Promise.resolve()
      .then(() => reportOptionsService.getReportSettings(clientId))
      .then((settings) => setClientAudited(clientId, isAuditedStatus(settings?.auditStatus, settings?.tradingStatus)))
      .catch(() => undefined)
      .finally(() => loading.delete(clientId));
  }, [clientId]);

  return audited;
}
